/**
 * Числа, время и единицы измерения — в одном месте.
 *
 * Traccar считает в метрах, узлах и миллисекундах; человек — в километрах,
 * километрах в час и часах. Перевод между ними жил в трёх экранах отдельными
 * копиями, причём с зашитыми русскими «км» и «ч» для всех языков сразу.
 * Выгрузка вынудила это свести: файл обязан совпадать с тем, что человек
 * видит на экране, иначе первым же вопросом будет «а почему цифры разные».
 */

import { knotsToKmh } from './api'
import type { Dict, Lang } from './i18n'

type NumOpts = {
  /** Обязательное число знаков после запятой. */
  min?: number
  /** Предельное число знаков после запятой. */
  max?: number
  /** Разделять ли разряды. В ячейке таблицы — нет: Excel примет это за текст. */
  grouping?: boolean
}

/** Число по правилам языка: у немцев и русских дробная часть через запятую. */
export function num(value: number, lang: Lang, opts: NumOpts = {}): string {
  const { min = 0, max = Math.max(min, 0), grouping = true } = opts
  return value.toLocaleString(lang, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: grouping,
  })
}

/** Пробег: Traccar отдаёт метры. */
export function km(meters: number, lang: Lang, grouping = true): string {
  return num(meters / 1000, lang, { min: 1, max: 1, grouping })
}

/** Скорость: Traccar отдаёт узлы. Дробные километры в час никому не нужны. */
export function kmh(knots: number, lang: Lang): string {
  return num(knotsToKmh(knots), lang, { grouping: false })
}

/**
 * Длительность десятичными часами — для таблицы.
 *
 * «2 ч 30 мин» читается лучше, но такую графу нельзя сложить, а файл выгрузки
 * существует ровно ради арифметики. На экране остаётся человеческий вид.
 */
export function hoursDecimal(ms: number, lang: Lang): string {
  return num(ms / 3_600_000, lang, { min: 2, max: 2, grouping: false })
}

/** Длительность для экрана: «2 ч 15 мин». */
export function duration(ms: number, lang: Lang, t: Dict): string {
  const total = Math.round(ms / 60000)
  const h = Math.floor(total / 60)
  const m = total % 60
  const minutes = `${num(m, lang)} ${t.unitMinute}`
  return h > 0 ? `${num(h, lang)} ${t.unitHour} ${minutes}` : minutes
}

/**
 * Дата и время.
 *
 * Часы всегда 24-часовые: в журнале выездов «08:00 PM» читается медленнее и
 * сортируется хуже, а половина парка и так живёт в 24-часовых странах.
 * Разделитель между датой и временем — пробел, а не запятая: запятую пришлось
 * бы экранировать в каждой английской выгрузке.
 */
export function dateTime(iso: string, lang: Lang, withYear = false): string {
  const moment = new Date(iso)
  const date = moment.toLocaleDateString(lang, {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' as const } : {}),
  })
  const time = moment.toLocaleTimeString(lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${date} ${time}`
}

/** Только время — для списка машин, где дата и так сегодняшняя. */
export function clock(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleTimeString(lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}
